import Receipt20 from '@carbon/icons-react/es/receipt/20';
import { FC } from 'react';
import styled from 'styled-components';
import { SupportedPlansType } from 'api/billing/types';
import FeatureScreenshot from 'assets/feature-gating/invoices-list-feature-gating.png';
import { PageLoading } from 'components/atoms/loading';
import { MaskableContent } from 'components/atoms/maskable-content';
import { FeatureGatingModal } from 'components/molecules/feature-gating/feature-gating-modal';
import { Helium } from 'components/templates';
import { useHasAccessToInvoicing } from 'hooks/use-invoice-permission';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { useCreditNoteEssentialSettingsQuery } from 'queries/credit-note-settings';
import { useFeatureSettings } from '../use-feature-setting';
import { Divider, Section } from './components';
import { CreditNoteTitle } from './credit-note-title';
import { DefaultNotesSettings } from './default-notes';
import { FeatureSwitch } from './feature-switch';
import { CreditNoteNumberSettings } from './note-number';
import { ReasonCodeSettings } from './reason-code';
import { ReasonDescriptionSettings } from './reason-description';

const Wrapper = styled.div`
  padding-bottom: var(--layout-07);
`;

export const CreditNoteEssentialSettings: FC = () => {
  const translate = useTranslate();
  const hasAccessToInvoicing = useHasAccessToInvoicing();
  const { data, isLoading } = useCreditNoteEssentialSettingsQuery({
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
  const documentTitle = useTranslation(
    'settings.credit_notes_essentials.title',
  );
  const [featureEnabled, setFeatureEnabled] = useFeatureSettings();

  return (
    <Helium icon={<Receipt20 />} documentTitle={documentTitle}>
      {!hasAccessToInvoicing && (
        <FeatureGatingModal
          planType={SupportedPlansType.Premium}
          title={translate('invoices.feature_gating.title')}
          description={translate('invoices.feature_gating.description')}
          featureImage={FeatureScreenshot}
          learnMoreLink="https://help.rocketlane.com/support/solutions/folders/67000581853"
        />
      )}
      <section>
        <FeatureSwitch
          enabled={featureEnabled}
          onStateChange={setFeatureEnabled}
        />
      </section>
      <Divider />
      <MaskableContent
        showMask={!featureEnabled}
        data-cy="timesheet-settings-content"
      >
        {isLoading || !data ? (
          <PageLoading />
        ) : (
          <Wrapper>
            <Section>
              <CreditNoteNumberSettings
                creditNotePrefix={data.creditNotePrefix}
                nextCreditNoteNumber={data.nextCreditNoteNumber}
                isAllowedToEditCreditNoteNumber={
                  data.allowUsersToEditCreditNoteNumber
                }
              />
            </Section>
            <Divider />
            <Section>
              <CreditNoteTitle creditNoteTitle={data.creditNoteDisplayTitle} />
            </Section>
            <Divider />
            <Section>
              <ReasonCodeSettings reasonMandatory={data.reasonMandatory} />
            </Section>
            <Divider />
            <Section>
              <ReasonDescriptionSettings
                reasonDescriptionMandatory={data.reasonDescriptionMandatory}
              />
            </Section>
            <Divider />
            <Section>
              <DefaultNotesSettings defaultNotes={data.defaultNotes} />
            </Section>
          </Wrapper>
        )}
      </MaskableContent>
    </Helium>
  );
};
